import React, { useCallback, useEffect, useState } from 'react';
import { Users, Check, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  listProfiles,
  getActiveProfileId,
  setActiveProfileId,
  type ViewerProfile,
} from '../../services/viewerProfileService';

/**
 * Who on this account is watching, and a way to change it.
 *
 * Remembers the choice rather than asking on every visit. Most accounts here are one
 * person, and making them pick themselves out of a list of one before they can watch
 * anything is friction with no purpose. A household switches when they need to, and the
 * browser remembers until they do.
 *
 * Switching reloads the page rather than re-fetching in place. Continue watching, resume
 * positions and everything else keyed to a profile live in components that each hold
 * their own copy, and a reload is the honest way to say "everything you are looking at
 * belongs to someone else now". A partial switch, where the row updates and the player
 * does not, would be worse than a visible reload.
 */
const ProfileSwitcher: React.FC = () => {
  const [profiles, setProfiles] = useState<ViewerProfile[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const activeId = getActiveProfileId();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setProfiles(await listProfiles());
    } catch {
      // Not worth a message. This is a convenience control, and a viewer whose profiles
      // could not be listed can still watch - as the default profile.
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      void load();
    }
  }, [open, load]);

  const switchTo = (profile: ViewerProfile) => {
    // The default profile is stored as "nothing chosen" rather than as its own id. Both
    // reach the same profile on the server, and it means a browser that has never picked
    // behaves identically to one that picked the main profile.
    setActiveProfileId(profile.isDefault ? null : profile.id);
    window.location.reload();
  };

  const active = profiles.find((p) => (activeId ? p.id === activeId : p.isDefault));

  return (
    <div className="relative">
      <button
        type="button"
        aria-label={active ? `Watching as ${active.name}. Change profile` : 'Choose who is watching'}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-md px-2 py-1 text-sm text-gray-300 transition-colors hover:bg-orange-500/10 hover:text-orange-400"
      >
        <Users className="h-4 w-4" aria-hidden="true" />
        <span className="hidden sm:inline">{active?.name ?? 'Profile'}</span>
      </button>

      {open && (
        <>
          {/* Click-away. Behind the menu, above everything else. */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden="true" />
          <div
            data-testid="profile-switcher-menu"
            className="absolute right-0 z-50 mt-2 w-56 rounded-xl border border-white/10 bg-slate-900/95 py-2 shadow-2xl backdrop-blur-md"
          >
            <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-[0.18em] text-orange-400/80">
              Watching as
            </p>

            {loading && <p className="px-3 py-2 text-sm text-gray-400">Loading…</p>}

            {!loading && profiles.length === 0 && (
              <p className="px-3 py-2 text-sm text-gray-400">No profiles yet.</p>
            )}

            {!loading && profiles.map((profile) => {
              const isActive = activeId ? profile.id === activeId : profile.isDefault;
              return (
                <button
                  key={profile.id}
                  type="button"
                  onClick={() => switchTo(profile)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-gray-200 hover:bg-orange-500/10 hover:text-orange-400"
                >
                  <span className="truncate">{profile.name}</span>
                  {isActive && <Check className="h-4 w-4 flex-shrink-0 text-orange-400" aria-label="Current" />}
                </button>
              );
            })}

            <div className="my-1 border-t border-white/10" />
            <Link
              to="/profiles"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-orange-500/10 hover:text-orange-400"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Manage profiles
            </Link>
          </div>
        </>
      )}
    </div>
  );
};

export default ProfileSwitcher;
